import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { TextCombobox } from '@/components/form/inputs/TextCombobox';
import { Highlight } from '@/components/form/inputs/combobox/ComboboxPopup';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from '@/hooks/useTranslation';
import type { FilterRule } from '../../type/dynamicChart';

type CategoricalValueInputProps = {
  rule: FilterRule;
  availableValues: string[];
  onChange: (updated: FilterRule) => void;
};

export function CategoricalValueInput({ rule, availableValues, onChange }: CategoricalValueInputProps) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const suggestions = useMemo(() => {
    const needle = text.trim().toLowerCase();
    return availableValues.filter(
      (value) => !rule.values.includes(value) && (!needle || value.toLowerCase().includes(needle))
    );
  }, [availableValues, rule.values, text]);

  const addChip = (value: string) => {
    if (value && !rule.values.includes(value)) {
      onChange({ ...rule, values: [...rule.values, value] });
    }
    setText('');
  };

  // A picked (or exactly typed) known value becomes a chip at once; free text waits for Enter.
  const handleChange = (value: string) => {
    if (availableValues.includes(value.trim())) addChip(value.trim());
    else setText(value);
  };

  const removeChip = (val: string) => {
    onChange({ ...rule, values: rule.values.filter((v) => v !== val) });
  };

  return (
    <div className="flex flex-1 flex-wrap items-center gap-1">
      {rule.values.map((val) => (
        <Badge key={val} className="text-foreground">
          {val}
          <button
            type="button"
            aria-label={t('common.removeItem', { item: val })}
            onClick={() => removeChip(val)}
            className="ml-0.5 rounded-full text-muted-foreground outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50"
          >
            <X className="h-3 w-3" aria-hidden />
          </button>
        </Badge>
      ))}
      <div className="min-w-[120px] flex-1">
        <TextCombobox<string>
          id={`filter-values-${rule.id}`}
          value={text}
          onChange={handleChange}
          onEnter={() => addChip(text.trim())}
          options={suggestions}
          optionValue={(value) => value}
          optionKey={(value) => value}
          renderOption={(value) => <Highlight text={value} query={text} />}
          emptyLabel=""
          hideWhenEmpty
          placeholder={t('analytics.dashboard.complexCharts.filters.valuePlaceholder')}
          ariaLabel={t('analytics.dashboard.complexCharts.filters.valuePlaceholder')}
        />
      </div>
    </div>
  );
}
